import React, { useMemo, useState } from 'react';

import { StatCard } from '../components/base';
import { LEAD_ORIGINS, LOSS_REASON_LABELS, STAGES } from '../lib/constantes';
import { baixarCSV } from '../lib/exportar';
import { fmt } from '../lib/formato';
import { mesAtual, mesesDisponiveis, resumoDoMes, rotuloMes, serieMensal, somarMes } from '../lib/mensal';

// ─── Visão mensal do pipeline ─────────────────────────────
// Um mês por vez (seletor) e a tabela mês a mês embaixo. A regra de cada número
// mora em lib/mensal.js, com testes; aqui é só apresentação.

const pct = (v) => v === null ? '—' : `${v.toFixed(0)}%`;
const dias = (v) => v === null ? '—' : `${v.toFixed(0)}d`;
const rotuloEtapa = (k) => STAGES.find(s => s.key === k)?.label || k;
const rotuloOrigem = (k) => {
  const o = LEAD_ORIGINS.find(x => x.key === k);
  return o ? `${o.emoji} ${o.label}` : 'Sem origem';
};

function LinhaLead({ l, direita, onOpenLead }) {
  return <div onClick={() => onOpenLead(l)}
    className="flex items-center justify-between gap-3 bg-black/[0.03] hover:bg-black/[0.06] rounded-xl px-3 py-2 cursor-pointer transition-colors">
    <div className="flex-1 min-w-0">
      <p className="text-sm font-medium text-gray-900 truncate">{l.company_name || 'Sem nome'}</p>
      <p className="t-caption-2 text-gray-400 truncate">{l.seller_name || 'sem vendedor'}</p>
    </div>
    <div className="flex items-center gap-2 flex-shrink-0">{direita}</div>
  </div>;
}

function Lista({ titulo, itens, vazio, children }) {
  return <div className="acard rounded-2xl p-5 flex flex-col">
    <h3 className="text-sm font-semibold text-gray-700 mb-3">{titulo} <span className="text-gray-400 font-normal">· {itens.length}</span></h3>
    {itens.length === 0
      ? <p className="text-sm text-gray-400 text-center py-6">{vazio}</p>
      : <div className="space-y-1.5 max-h-[340px] overflow-y-auto">{children}</div>}
  </div>;
}

function Barras({ titulo, linhas, cor }) {
  if (!linhas.length) return null;
  const max = Math.max(...linhas.map(x => x.qtd), 1);
  return <div className="acard rounded-2xl p-5">
    <h3 className="text-sm font-semibold text-gray-700 mb-3">{titulo}</h3>
    <div className="space-y-2">
      {linhas.map(x => <div key={x.rotulo} className="flex items-center gap-3">
        <span className="text-xs text-gray-600 w-36 truncate">{x.rotulo}</span>
        <div className="flex-1 bg-gray-100 rounded-full h-3 overflow-hidden">
          <div className={`h-full rounded-full ${cor}`} style={{ width: `${(x.qtd / max) * 100}%` }} />
        </div>
        <span className="text-xs font-semibold text-gray-700 w-8 text-right">{x.qtd}</span>
        {x.valor !== undefined && <span className="text-xs text-gray-500 w-24 text-right">{fmt(x.valor)}</span>}
      </div>)}
    </div>
  </div>;
}

function MesView({ leads, onOpenLead }) {
  const atual = mesAtual();
  const [mes, setMes] = useState(atual);
  const meses = useMemo(() => mesesDisponiveis(leads), [leads]);
  const r = useMemo(() => resumoDoMes(leads, mes), [leads, mes]);
  const serie = useMemo(() => serieMensal(leads, 12), [leads]);
  const primeiro = meses[meses.length - 1];

  const etapas = STAGES.filter(s => r.porEtapa[s.key])
    .map(s => ({ rotulo: s.label, qtd: r.porEtapa[s.key].count, valor: r.porEtapa[s.key].value }));
  const motivos = Object.entries(r.motivos).sort((a, b) => b[1] - a[1])
    .map(([k, qtd]) => ({ rotulo: LOSS_REASON_LABELS[k] || 'Sem motivo informado', qtd }));
  const origens = Object.entries(r.origens).sort((a, b) => b[1] - a[1])
    .map(([k, qtd]) => ({ rotulo: rotuloOrigem(k), qtd }));

  const exportar = () => baixarCSV('pipeline-mes-a-mes', serie.map(s => ({
    'Mês': rotuloMes(s.mes), 'Entraram': s.entraram, 'Ganhos': s.ganhos, 'Valor ganho': s.ganho_valor,
    'Perdidos': s.perdidos, 'Valor perdido': s.perdido_valor, 'Em aberto no fim do mês': s.abertos,
    'Valor em aberto': s.aberto_valor, 'Conversão (%)': s.conversao === null ? '' : s.conversao.toFixed(1),
  })));

  return <div className="flex flex-col gap-5">
    <div className="flex items-center justify-between gap-3 flex-wrap">
      <h2 className="text-lg font-bold text-gray-800">Pipeline do mês</h2>
      <div className="flex items-center gap-2 flex-wrap">
        <button aria-label="Mês anterior" disabled={mes <= primeiro} onClick={() => setMes(somarMes(mes, -1))}
          className="w-9 h-9 rounded-xl border border-gray-200 bg-white text-gray-600 hover:border-gray-300 disabled:opacity-40">‹</button>
        <select className="apple-input px-3 py-2 text-sm" value={mes} onChange={e => setMes(e.target.value)}>
          {meses.map(m => <option key={m} value={m}>{rotuloMes(m)}{m === atual ? ' (atual)' : ''}</option>)}
        </select>
        <button aria-label="Próximo mês" disabled={mes >= atual} onClick={() => setMes(somarMes(mes, 1))}
          className="w-9 h-9 rounded-xl border border-gray-200 bg-white text-gray-600 hover:border-gray-300 disabled:opacity-40">›</button>
        {mes !== atual && <button onClick={() => setMes(atual)}
          className="text-sm px-3 py-2 rounded-xl border border-gray-200 bg-white text-gray-600 hover:border-gray-300">Mês atual</button>}
      </div>
    </div>

    {/* Resultado do mês */}
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
      <StatCard label="Entraram" value={r.entraram.length} color="text-blue-600" sub="leads novos no mês" />
      <StatCard label="Ganhos" value={r.ganhos.length} color="text-green-600" sub={fmt(r.ganho_valor)} />
      <StatCard label="Perdidos" value={r.perdidos.length} color={r.perdidos.length ? 'text-red-500' : 'text-gray-500'} sub={fmt(r.perdido_valor)} />
      <StatCard label="Conversão" value={pct(r.conversao)}
        color={r.conversao === null ? 'text-gray-400' : r.conversao >= 50 ? 'text-green-600' : 'text-orange-500'}
        sub="ganhos ÷ (ganhos + perdidos)" />
      <StatCard label="Ciclo médio" value={dias(r.ciclo_medio)} sub="da entrada ao fechamento" />
    </div>

    {/* Dinheiro */}
    <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3">
      <StatCard label="💰 Faturamento fechado" value={fmt(r.ganho_valor)} color="text-green-700"
        sub={r.ganho_contrato !== r.ganho_valor ? `Contrato total: ${fmt(r.ganho_contrato)}` : 'soma dos ganhos do mês'} />
      <StatCard label="🎯 Faturamento previsto" value={r.passado ? fmt(r.ganho_valor) : fmt(r.previsto_total)} color="text-purple-700"
        sub={r.passado ? 'mês encerrado: previsto = fechado'
          : `fechado + ${fmt(r.previsto_pipe)} do pipe com previsão no mês (${r.comPrevisao.length})`} />
      <StatCard label={r.atual ? '⏳ Parado no pipe' : '⏳ Em aberto no fim do mês'} value={fmt(r.aberto_valor)} color="text-blue-700"
        sub={`${r.abertos.length} lead${r.abertos.length === 1 ? '' : 's'} em aberto`} />
      <StatCard label="Ticket médio" value={r.ticket_medio === null ? '—' : fmt(r.ticket_medio)} color="text-teal-700" sub="por contrato ganho" />
    </div>

    {!r.atual && <p className="text-xs text-gray-400 -mt-2">
      Em meses passados, o que estava em aberto no fim do mês aparece com o valor e a etapa de hoje: o painel não guarda a foto de cada dia.
    </p>}

    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <Barras titulo={r.atual ? 'Pipe aberto por etapa' : 'Em aberto no fim do mês, por etapa de hoje'} linhas={etapas} cor="bg-blue-500" />
      <Barras titulo="Por que perdemos" linhas={motivos} cor="bg-red-400" />
      <Barras titulo="De onde vieram os leads novos" linhas={origens} cor="bg-sky-400" />
    </div>

    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <Lista titulo="Ganhos" itens={r.ganhos} vazio="Nenhum fechamento no mês.">
        {r.ganhos.map(l => <LinhaLead key={l.id} l={l} onOpenLead={onOpenLead}
          direita={<span className="text-xs font-bold text-green-700">{fmt(l.value)}</span>} />)}
      </Lista>
      <Lista titulo="Perdidos" itens={r.perdidos} vazio="Nenhuma perda no mês.">
        {r.perdidos.map(l => <LinhaLead key={l.id} l={l} onOpenLead={onOpenLead}
          direita={<span className="t-caption-2 px-2 py-0.5 rounded-full bg-red-100 text-red-600 font-medium">{LOSS_REASON_LABELS[l.loss_reason] || 'sem motivo'}</span>} />)}
      </Lista>
      <Lista titulo={r.atual ? 'Parados no pipe' : 'Em aberto no fim do mês'} itens={r.abertos} vazio="Nada em aberto.">
        {[...r.abertos].sort((a, b) => (Number(b.value) || 0) - (Number(a.value) || 0)).map(l => <LinhaLead key={l.id} l={l} onOpenLead={onOpenLead}
          direita={<>
            <span className="t-caption-2 text-gray-400">{rotuloEtapa(l.stage)}</span>
            {l.value > 0 && <span className="text-xs font-bold text-blue-700">{fmt(l.value)}</span>}
          </>} />)}
      </Lista>
    </div>

    {/* Mês a mês */}
    <div className="acard rounded-2xl p-5">
      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <h3 className="text-sm font-semibold text-gray-700">Mês a mês</h3>
        <button onClick={exportar}
          className="flex items-center gap-1.5 text-xs bg-white border border-gray-200 text-gray-600 rounded-xl px-3 py-2 hover:bg-gray-50">
          <span aria-hidden="true">⬇</span> CSV
        </button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm tabular">
          <thead>
            <tr className="text-left t-caption-2 text-gray-400 border-b border-black/[0.06]">
              <th className="py-2 pr-3 font-semibold">Mês</th>
              <th className="py-2 px-3 font-semibold text-right">Entraram</th>
              <th className="py-2 px-3 font-semibold text-right">Ganhos</th>
              <th className="py-2 px-3 font-semibold text-right">Faturamento</th>
              <th className="py-2 px-3 font-semibold text-right">Perdidos</th>
              <th className="py-2 px-3 font-semibold text-right">Valor perdido</th>
              <th className="py-2 px-3 font-semibold text-right">Em aberto no fim</th>
              <th className="py-2 pl-3 font-semibold text-right">Conversão</th>
            </tr>
          </thead>
          <tbody>
            {serie.map(s => <tr key={s.mes} onClick={() => setMes(s.mes)}
              className={`border-b border-black/[0.04] cursor-pointer hover:bg-black/[0.03] ${s.mes === mes ? 'bg-blue-50/60' : ''}`}>
              <td className="py-2 pr-3 font-medium text-gray-800 whitespace-nowrap">{rotuloMes(s.mes)}</td>
              <td className="py-2 px-3 text-right">{s.entraram}</td>
              <td className="py-2 px-3 text-right text-green-700 font-semibold">{s.ganhos}</td>
              <td className="py-2 px-3 text-right text-green-700">{fmt(s.ganho_valor)}</td>
              <td className="py-2 px-3 text-right text-red-500">{s.perdidos}</td>
              <td className="py-2 px-3 text-right text-gray-500">{fmt(s.perdido_valor)}</td>
              <td className="py-2 px-3 text-right text-blue-700">{s.abertos} · {fmt(s.aberto_valor)}</td>
              <td className="py-2 pl-3 text-right">{pct(s.conversao)}</td>
            </tr>)}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-gray-400 mt-3">Clique num mês para abrir o detalhe acima. Ganho conta no mês do fechamento, perda no mês da perda, entrada no mês de criação.</p>
    </div>
  </div>;
}

export { MesView };
